import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { newsService } from '../../api/newsService';
import { mapNewsItems } from '../../utils/newsMapper';

/**
 * Async thunk to fetch the latest IPO news headlines
 * GET /api/market/news
 *
 * options.maxAgeMs: skip the request when data this recent is already loaded
 */
export const fetchNews = createAsyncThunk(
  'news/fetchNews',
  async (_options, { rejectWithValue }) => {
    try {
      const data = await newsService.getNews();
      if (data?.success === false) {
        return rejectWithValue(data.error || 'Failed to fetch news');
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch news');
    }
  },
  {
    condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
      const { status, fetchedAt } = getState().news;
      // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
      if (status === 'loading') return false;
      if (maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs) {
        return false;
      }
      return true;
    },
  }
);

const initialState = {
  // Raw items from the API; views map them through selectNewsArticles
  items: [],
  refreshedAt: null,
  source: null,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

export const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNews.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchNews.fulfilled, (state, action) => {
        const data = action.payload?.data || {};
        state.status = 'succeeded';
        state.items = Array.isArray(data.items) ? data.items : [];
        state.refreshedAt = data.refreshed_at || null;
        state.source = data.source || null;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchNews.rejected, (state, action) => {
        // Keep previously loaded items so a failed refresh doesn't blank the section
        state.status = 'failed';
        state.error = action.payload || 'Failed to load news';
      });
  },
});

const selectNewsItems = (state) => state.news.items;

// Newest-first, display-ready articles for the landing-page news section
export const selectNewsArticles = createSelector([selectNewsItems], (items) => mapNewsItems(items));

export default newsSlice.reducer;
